import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

export const InvalidAddress = () => {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  const [isHexVisible, setIsHexVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHexVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="mt-10 flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-2xl"
      >
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="overflow-hidden pt-0">
            <div className="h-2 bg-destructive" />
            <div className="p-8 md:p-10">
              <div className="mb-6 flex items-center gap-4">
                <motion.div
                  className="relative flex-shrink-0"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                >
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 10, 0] }}
                    transition={{
                      duration: 0.5,
                      repeat: Number.POSITIVE_INFINITY,
                      repeatDelay: 5,
                    }}
                  >
                    <div className="rounded-full bg-destructive/10 p-3">
                      <AlertCircle size={32} className="text-destructive" />
                    </div>
                  </motion.div>
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.8, 0, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Number.POSITIVE_INFINITY,
                      repeatDelay: 2,
                    }}
                  >
                    <div className="h-full w-full rounded-full bg-destructive/20" />
                  </motion.div>
                </motion.div>

                <div className="flex-1">
                  <motion.h1
                    className="text-2xl font-bold text-slate-900 md:text-3xl"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Invalid Address Format
                  </motion.h1>
                </div>
              </div>

              <motion.p
                className="mb-6 text-lg leading-relaxed text-slate-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                The address format is not recognized. Please check the address
                and try again.
              </motion.p>

              {address && (
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{
                    opacity: isHexVisible ? 1 : 0,
                    height: isHexVisible ? 'auto' : 0,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-slate-50 p-5">
                    <div className="absolute right-0 top-0 h-20 w-20 -translate-y-1/2 translate-x-1/2 rounded-full bg-slate-100 opacity-50" />
                    <div className="absolute bottom-0 left-0 h-16 w-16 -translate-x-1/2 translate-y-1/2 rounded-full bg-slate-100 opacity-50" />

                    <p className="mb-2 text-sm font-medium text-slate-500">
                      Attempted address:
                    </p>
                    <div className="relative break-all font-mono text-sm md:text-base">
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                          staggerChildren: 0.005,
                          delayChildren: 0.1,
                        }}
                        className="text-destructive"
                      >
                        {address.split('').map((char, i) => (
                          <motion.span
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.005 }}
                          >
                            {char}
                          </motion.span>
                        ))}
                      </motion.span>
                      <motion.div
                        className="absolute bottom-0 left-0 h-[2px] bg-destructive"
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-4 flex justify-center"
              >
                <Button
                  variant="secondary"
                  size="xl"
                  onClick={() => navigate('/')}
                >
                  Go Back Home
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center text-sm text-slate-500"
              >
                Please contact support if you continue to experience issues
              </motion.div>
            </div>
          </Card>
        </motion.div>
      </motion.div>

      <motion.div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 0.8 }}
      >
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-destructive/10"
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.2, 0],
              scale: [0, 1, 1.5],
              x: Math.random() * 100 - 50,
              y: Math.random() * 100 - 50,
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Number.POSITIVE_INFINITY,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </motion.div>
    </div>
  );
};
